import { useCallback, useEffect, useState } from "react";
import { getBoardReport, getBoardReportRun } from "./document/boardReportApi.js";

/**
 * useBoardReportDocument - loads a board report + its run detail (period,
 * narrative, drivers, resolved manifest) in parallel, mirroring
 * BoardReportPrint's original inline `loadData`.
 *
 * Loads whenever both `id` and `runId` are truthy; callers that need to gate
 * loading on something else (e.g. BoardReportPrint's keycloak-token check)
 * do so by not calling this hook with real ids until that condition holds,
 * or by treating a still-loading/no-op state as "not ready yet".
 */
const useBoardReportDocument = (id, runId) => {
  const [boardReport, setBoardReport] = useState(null);
  const [run, setRun] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!id || !runId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const [report, runDetail] = await Promise.all([
        getBoardReport(id),
        getBoardReportRun(id, runId),
      ]);
      setBoardReport(report);
      setRun(runDetail.run);
      setManifest(runDetail.manifest);
    } catch (err) {
      console.error("Failed to load board report:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id, runId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { boardReport, run, manifest, isLoading, error, reload: loadData };
};

export default useBoardReportDocument;
